// Telemetría en vivo vía Firebase Realtime Database — espejo del QueueTelemetry
// de la app Android nativa. Escribe en la MISMA estructura para verse junto a
// las tablets en el visor del portal: /telemetry/{controlador}/{dispositivo}.
//
// La PWA se distingue por `plataforma: 'pwa'` y el prefijo `pwa-` del id de
// dispositivo. Todo es best-effort: si Firebase no está configurado (config
// vacía) o falla, la app sigue funcionando exactamente igual.

import { getDatabase, ref, push, update, serverTimestamp, query, orderByChild, endAt, get, type Database } from 'firebase/database';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirebaseApp } from './firebase';
import { getUsername } from './storage';
import { esCancelacionPropia } from '../utils/errors';

let db: Database | null = null;
let authStarted = false;

// Las reglas de RTDB exigen sesión: una escritura lanzada ANTES de que el login
// anónimo termine se rechaza y se pierde (el SDK no la reintenta). Bufferizamos
// lo emitido durante ese arranque y lo volcamos cuando la auth está lista.
let authReady = false;
let pendingStatus: Record<string, unknown> | null = null;
const pendingLogs: Array<Record<string, unknown>> = [];
const MAX_PENDING_LOGS = 100;

// Retención del historial: cada app limpia SU propio nodo de eventos (48h).
// El status (foto viva) no caduca. Throttle de 6h entre pasadas de limpieza.
const EVENT_RETENTION_MS = 48 * 60 * 60 * 1000;
const PURGE_THROTTLE_MS = 6 * 60 * 60 * 1000;
const PURGE_KEY = 'TELEMETRY_LAST_PURGE';

const DEVICE_KEY = 'TELEMETRY_DEVICE_ID';
const NETWORK_REFRESH_MS = 5 * 60 * 1000;
const MAX_PUBLIC_IPS = 8;
let networkSnapshot: Record<string, unknown> = {};
let lastNetworkWrite = 0;

function deviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = 'pwa-' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return 'pwa-desconocido';
  }
}

// Modelo del dispositivo: en Android el user agent trae "…; SM-X200 Build/…".
// En otros casos caemos a la plataforma del navegador.
function deviceModel(): string {
  try {
    const ua = navigator.userAgent || '';
    const androidModel = /;\s*([^;)]+?)\s+Build\//.exec(ua);
    if (androidModel) return androidModel[1].trim();
    const uaData = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
    if (uaData && uaData.platform) return uaData.platform;
    const parens = /\(([^)]+)\)/.exec(ua);
    return parens ? parens[1].split(';')[0].trim() : 'navegador';
  } catch {
    return 'navegador';
  }
}

// Clave del controlador: correo logado saneado para RTDB (sin . # $ [ ] /).
// null SIN SESIÓN — y entonces no se emite nada: un evento que no se puede
// atribuir a nadie no sirve para diagnosticar, y todos los dispositivos sin
// sesión acababan mezclados en un mismo nodo que solo ensuciaba el visor.
function controllerKey(): string | null {
  const user = (getUsername() || '').trim().toLowerCase();
  const clean = user.replace(/[.#$[\]/]/g, '_');
  return clean || null;
}

export function initTelemetry(): void {
  if (db) return;
  const app = getFirebaseApp();
  const config = __FIREBASE_CONFIG__;
  if (!app || !config.databaseURL) return;
  try {
    db = getDatabase(app, config.databaseURL);
    collectBrowserLocalIps()
      .then((localIps) => {
        if (localIps.length > 0) updateNetworkTelemetry({ localIps });
      })
      .catch(() => { /* best-effort: la telemetría de red nunca rompe el arranque */ });
    if (!authStarted) {
      authStarted = true;
      const auth = getAuth(app);
      onAuthStateChanged(auth, (user) => {
        if (user && !authReady) {
          authReady = true;
          flushPending();
        }
      });
      // Las reglas de RTDB exigen sesión: auth anónima, en segundo plano.
      signInAnonymously(auth).catch(() => { /* sin telemetría; la app sigue */ });
    }
    installCrashHandler();
  } catch {
    db = null;
  }
}

// Guarda la IP publica que el backend ha observado. Se invoca desde el
// interceptor HTTP leyendo X-Observed-Client-IP; nunca se confia en un valor
// calculado por JavaScript para representar la IP publica.
//
// CRITICO: corre en el camino de respuesta de axios (exito y error). Va
// envuelto en try/catch — como el resto de telemetria: NUNCA puede lanzar,
// o convertiria una respuesta 200 en promesa rechazada y romperia la carga
// de datos de la pantalla.
export function telemetryObservedPublicIp(value: unknown): void {
  try {
    const publicIp = normalizeIp(value);
    if (!publicIp) return;
    // Fast-path del caso comun (IP estable dentro de la ventana): esto se
    // ejecuta en CADA respuesta HTTP, así que evitamos reconstruir arrays
    // cuando no hay nada nuevo que escribir.
    if (publicIp === networkSnapshot.publicIp && Date.now() - lastNetworkWrite < NETWORK_REFRESH_MS) return;
    const previous = Array.isArray(networkSnapshot.publicIps)
      ? networkSnapshot.publicIps.filter((item): item is string => typeof item === 'string')
      : [];
    const publicIps = [publicIp, ...previous.filter((item) => item !== publicIp)].slice(0, MAX_PUBLIC_IPS);
    updateNetworkTelemetry({
      publicIp,
      publicIps,
      addressFamily: publicIp.includes(':') ? 'IPv6' : 'IPv4',
      source: 'backend',
    });
  } catch { /* la telemetria jamas debe romper el interceptor HTTP */ }
}

function updateNetworkTelemetry(fields: Record<string, unknown>): void {
  try {
    const changed = Object.keys(fields).some((key) =>
      JSON.stringify(networkSnapshot[key]) !== JSON.stringify(fields[key]));
    networkSnapshot = { ...networkSnapshot, ...fields };
    if (!changed && Date.now() - lastNetworkWrite < NETWORK_REFRESH_MS) return;
    lastNetworkWrite = Date.now();
    telemetryStatus({
      network: {
        ...networkSnapshot,
        observedAt: Date.now(),
      },
    });
  } catch { /* best-effort */ }
}

// Los navegadores modernos suelen sustituir las IP locales por nombres mDNS.
// Conservamos unicamente candidatos ICE que sean direcciones privadas reales;
// si el navegador no las expone, no se registra ningun dato ficticio.
async function collectBrowserLocalIps(): Promise<string[]> {
  if (typeof RTCPeerConnection === 'undefined') return [];
  const addresses = new Set<string>();
  let peer: RTCPeerConnection | null = null;
  try {
    peer = new RTCPeerConnection({ iceServers: [] });
    const pc = peer;
    pc.createDataChannel('network-telemetry');
    pc.addEventListener('icecandidate', (event) => {
      const parts = event.candidate?.candidate.split(/\s+/) || [];
      const candidate = normalizeIp(parts[4]);
      if (candidate && isPrivateIp(candidate)) addresses.add(candidate);
    });
    await pc.setLocalDescription(await pc.createOffer());
    // Resolvemos cuando ICE termina de recolectar (rapido en la mayoria de
    // equipos) o a los 2s como tope — lo que ocurra antes. Evita esperar
    // siempre un tiempo fijo.
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      const timer = setTimeout(finish, 2000);
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') { clearTimeout(timer); finish(); }
      });
    });
  } catch { /* el navegador puede bloquear WebRTC; telemetria best-effort */ }
  finally { peer?.close(); }
  return Array.from(addresses);
}

function normalizeIp(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const candidate = value.trim().replace(/^\[|\]$/g, '');
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(candidate)) {
    return candidate.split('.').every((part) => Number(part) <= 255) ? candidate : null;
  }
  return candidate.length <= 45 && candidate.includes(':') && /^[0-9a-f:.]+$/i.test(candidate)
    ? candidate.toLowerCase()
    : null;
}

function isPrivateIp(value: string): boolean {
  if (value.includes(':')) {
    return /^(?:fc|fd|fe8|fe9|fea|feb)/i.test(value) || value === '::1';
  }
  const octets = value.split('.').map(Number);
  return octets[0] === 10 || octets[0] === 127 ||
    (octets[0] === 169 && octets[1] === 254) ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) ||
    (octets[0] === 192 && octets[1] === 168);
}

function flushPending(): void {
  try {
    if (!db) return;
    const key = controllerKey();
    if (!key) {
      // Sesión cerrada mientras la auth anónima arrancaba: lo bufferizado ya no
      // es atribuible, así que se descarta en vez de colgarlo de nadie.
      pendingLogs.length = 0;
      pendingStatus = null;
      return;
    }
    const eventsRef = ref(db, `telemetry/${key}/${deviceId()}/events`);
    while (pendingLogs.length > 0) {
      const payload = pendingLogs.shift();
      if (payload) push(eventsRef, payload).catch(() => { /* best-effort */ });
    }
    if (pendingStatus) {
      const fields = pendingStatus;
      pendingStatus = null;
      telemetryStatus(fields);
    }
  } catch { /* best-effort */ }
}

export function telemetryLog(evento: string, detalles: Record<string, unknown> = {}): void {
  try {
    if (!db) return;
    // Sin sesión no se emite nada: el evento no sería atribuible a ningún
    // controlador. Cubre todo lo que pasa antes del login (el propio intento
    // de login incluido) y todo lo posterior a cerrar sesión.
    const key = controllerKey();
    if (!key) return;
    const payload = {
      ...detalles,
      evento,
      horaLocal: Date.now(),
      hora: serverTimestamp(),
    };
    if (!authReady) {
      // Auth anónima aún en curso: se bufferiza para no perder el evento.
      if (pendingLogs.length < MAX_PENDING_LOGS) pendingLogs.push(payload);
      return;
    }
    push(ref(db, `telemetry/${key}/${deviceId()}/events`), payload)
      .catch(() => { /* best-effort */ });
  } catch { /* best-effort */ }
}

export function telemetryStatus(fields: Record<string, unknown>): void {
  try {
    if (!db) return;
    const key = controllerKey();
    if (!key) return;
    if (!authReady) {
      pendingStatus = { ...(pendingStatus || {}), ...fields };
      return;
    }
    update(ref(db, `telemetry/${key}/${deviceId()}/status`), {
      ...fields,
      ultimaSenal: serverTimestamp(),
      version: __APP_VERSION__,
      plataforma: 'pwa',
      modelo: deviceModel(),
      controlador: getUsername(),
    }).catch(() => { /* best-effort */ });
  } catch { /* best-effort */ }
}

// Borra los eventos de ESTE dispositivo con más de 48h. Un solo update con
// todas las claves a null = una escritura. Best-effort, con throttle de 6h.
export function purgeOldTelemetryEvents(): void {
  try {
    if (!db) return;
    const key = controllerKey();
    if (!key) return;
    const last = Number(localStorage.getItem(PURGE_KEY) || 0);
    if (Date.now() - last < PURGE_THROTTLE_MS) return;
    const eventsRef = ref(db, `telemetry/${key}/${deviceId()}/events`);
    const q = query(eventsRef, orderByChild('horaLocal'), endAt(Date.now() - EVENT_RETENTION_MS));
    get(q)
      .then((snap) => {
        const updates: Record<string, null> = {};
        snap.forEach((child) => {
          if (child.key) updates[child.key] = null;
        });
        if (Object.keys(updates).length > 0) {
          update(eventsRef, updates).catch(() => { /* best-effort */ });
        }
        localStorage.setItem(PURGE_KEY, String(Date.now()));
      })
      .catch(() => { /* sin permisos aún o sin red — se reintenta en la próxima apertura */ });
  } catch { /* best-effort */ }
}

// Error con traza, visible EN VIVO en el visor — espejo del error() de Android.
// `origen` identifica el punto del código ("guardado", "sync"…) para poder
// localizarlo sin adivinar. Sentry sigue siendo el registro permanente; esto es
// lo que permite diagnosticar a distancia a un controlador que está en el campo
// ahora mismo, sin esperar a que el evento aparezca en Sentry.
const MAX_STACK_CHARS = 3000;

export function telemetryError(origen: string, error: unknown, detalles: Record<string, unknown> = {}): void {
  try {
    if (esCancelacionPropia(error)) return;
    const err = error as { name?: string; message?: string; stack?: string } | null | undefined;
    const payload: Record<string, unknown> = {
      ...detalles,
      origen,
      tipo: err?.name || (error === null || error === undefined ? 'desconocido' : typeof error),
      mensaje: err?.message || String(error ?? '(sin mensaje)'),
    };
    if (err?.stack) payload.traza = err.stack.slice(0, MAX_STACK_CHARS);
    telemetryLog('error', payload);
  } catch { /* la telemetría jamás rompe el reporte de errores */ }
}


// Fallos que NO son de la app y solo ensucian el visor. Los handlers globales
// se enteran de todo lo que revienta en la página, venga de donde venga:
//  - `@vite/client`: el canal HMR del servidor de desarrollo, que al arrancar
//    lanza "send was called before connect" hasta que abre su WebSocket. En una
//    sola sesión de pruebas generó 23 eventos. No existe en producción, pero
//    ahogaba los errores de verdad mientras se desarrolla.
//  - Extensiones del navegador: revientan en su propio contexto y no dicen nada
//    de la app.
function esRuidoExterno(error: unknown): boolean {
  const err = error as { stack?: string; message?: string } | null | undefined;
  const texto = `${err?.stack ?? ''} ${err?.message ?? ''}`;
  return /@vite\/client|vite\/dist\/client|chrome-extension:|moz-extension:|safari-extension:/.test(texto);
}

// Excepciones que nadie captura: se registran ANTES de que la pantalla se
// quede a medias, para verlas en vivo en el visor (espejo del crash handler de
// Android). Se instala una sola vez desde initTelemetry.
let crashHandlerInstalled = false;
function installCrashHandler(): void {
  if (crashHandlerInstalled) return;
  crashHandlerInstalled = true;
  try {
    window.addEventListener('error', (e) => {
      const error = e.error ?? new Error(e.message);
      if (esRuidoExterno(error) || /@vite\/client/.test(e.filename ?? '')) return;
      telemetryError('crash', error, { fichero: e.filename, linea: e.lineno });
      telemetryStatus({ estado: 'crash', ultimoError: (e.error as Error | undefined)?.name || 'Error' });
    });
    window.addEventListener('unhandledrejection', (e) => {
      if (esRuidoExterno(e.reason)) return;
      telemetryError('promesa_sin_capturar', e.reason);
    });
  } catch { /* sin window (SW/SSR): sin handler */ }
}

// Actividad HTTP (método, ruta, código, duración). Nunca cuerpos ni tokens.
export function telemetryHttp(method: string, path: string, code: number, ms: number, error?: string): void {
  // Una petición que abortamos nosotros (modo offline) no es una petición sin
  // respuesta: se distingue para no leerla como caída de red al revisar el
  // visor. El evento se conserva —saber QUÉ se abortó ayuda a reconstruir lo
  // que hizo el controlador— pero deja de disfrazarse de fallo.
  const cancelada = error === 'ERR_CANCELED' || error === 'CanceledError';
  const detalles: Record<string, unknown> = {
    peticion: `${method} ${path}`,
    codigo: code > 0 ? code : cancelada ? 'cancelada' : 'sin respuesta',
    ms,
  };
  if (error) detalles.error = error;
  telemetryLog('http', detalles);
}
